import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Table,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { cameraAPI, snapshotsAPI, cameraDeskZonesAPI } from '../utils/api';
import { formatDateTimeInCompanyTimezone } from '../utils/timezone';
import type { Camera } from '../types/camera';
import type { Snapshot } from '../types/snapshot';
import type { CameraDeskZone } from '../types/deskOccupancy';

const DESK_KEY_PATTERN = /^[a-zA-Z0-9_-]+$/;

/** Local editor state — localId is UI-only and never sent to the API. */
type EditableDeskZone = CameraDeskZone & { localId: string };

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const toApiZone = (zone: EditableDeskZone): CameraDeskZone => ({
  desk_key: zone.desk_key.trim(),
  label: zone.label.trim(),
  x1: zone.x1,
  y1: zone.y1,
  x2: zone.x2,
  y2: zone.y2,
  active: zone.active,
  sort_order: Number(zone.sort_order) || 0,
});

const DeskZonesPage: React.FC = () => {
  const { currentCompany } = useAuth();
  const { canViewSnapshots, canEditSnapshotSettings } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'deskZones' });
  const { t: tc } = useTranslation('common');

  const [cameras, setCameras] = useState<Camera[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<number | null>(null);
  const [zones, setZones] = useState<EditableDeskZone[]>([]);
  const [loadedZones, setLoadedZones] = useState<EditableDeskZone[]>([]);
  const [referenceSnapshot, setReferenceSnapshot] = useState<Snapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [selectedLocalId, setSelectedLocalId] = useState<string | null>(null);
  const localIdCounterRef = useRef(0);
  const imageRectRef = useRef<{ width: number; height: number; left: number; top: number } | null>(null);
  const editorSurfaceRef = useRef<HTMLDivElement>(null);
  const [draftZone, setDraftZone] = useState<{ x1: number; y1: number; x2: number; y2: number; final: boolean } | null>(null);
  const dragRef = useRef<{
    mode: 'draw' | 'move' | 'resize';
    index: number;
    corner?: 'tl' | 'tr' | 'bl' | 'br';
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
    baseZone: EditableDeskZone;
    draft?: { x1: number; y1: number; x2: number; y2: number };
  } | null>(null);

  const createLocalId = () => {
    localIdCounterRef.current += 1;
    return `zone_${localIdCounterRef.current}`;
  };

  const toEditableZones = (source: CameraDeskZone[]): EditableDeskZone[] =>
    source.map(zone => ({ ...zone, localId: createLocalId() }));

  const canEdit = canViewSnapshots && canEditSnapshotSettings;

  const fetchCameras = useCallback(async () => {
    if (!currentCompany) return;
    try {
      const res = await cameraAPI.getCameras(currentCompany.company_id);
      setCameras(res.cameras || []);
      setSelectedCameraId(prev => prev ?? (res.cameras || [])[0]?.id ?? null);
    } catch (err) {
      console.error('Error fetching cameras:', err);
      setError(t('fetchFailed'));
    }
  }, [currentCompany, t]);

  const loadZones = useCallback(async (cameraId: number) => {
    if (!currentCompany) return;
    try {
      setLoading(true);
      setError(null);
      const [zonesRes, snapshotsRes] = await Promise.all([
        cameraDeskZonesAPI.getZones(currentCompany.company_id, cameraId),
        snapshotsAPI.listSnapshots(currentCompany.company_id, { camera_id: cameraId, per_page: 1, page: 1 }),
      ]);
      const storedZones = toEditableZones(zonesRes.data ?? []);
      setZones(storedZones.map(zone => ({ ...zone })));
      setLoadedZones(storedZones.map(zone => ({ ...zone })));
      setReferenceSnapshot(snapshotsRes.data?.[0] ?? null);
      setSelectedLocalId(storedZones[0]?.localId ?? null);
      setDraftZone(null);
    } catch (err) {
      console.error('Error loading desk zones:', err);
      setError(t('fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentCompany, t]);

  useEffect(() => {
    if (currentCompany) fetchCameras();
  }, [currentCompany, fetchCameras]);

  useEffect(() => {
    if (currentCompany && selectedCameraId != null) {
      loadZones(selectedCameraId);
    }
  }, [currentCompany, selectedCameraId, loadZones]);

  const updateImageRect = () => {
    const element = editorSurfaceRef.current;
    if (!element) return imageRectRef.current;
    const rect = element.getBoundingClientRect();
    const next = { width: rect.width, height: rect.height, left: rect.left, top: rect.top };
    imageRectRef.current = next;
    return next;
  };

  const toPercentFromEvent = (e: { clientX: number; clientY: number }) => {
    const rect = updateImageRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) return { x: 0, y: 0 };
    const x = clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100);
    const y = clamp(((e.clientY - rect.top) / rect.height) * 100, 0, 100);
    return { x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 };
  };

  const pointInZone = (zone: EditableDeskZone, x: number, y: number) =>
    x >= zone.x1 && x <= zone.x2 && y >= zone.y1 && y <= zone.y2;

  const computeNextDeskKey = () => {
    const idx = zones.length + 1;
    let key = `desk_${idx}`;
    while (zones.some(z => z.desk_key === key)) {
      key = `desk_${idx}_${Math.floor(Math.random() * 1000)}`;
    }
    return key;
  };

  const handleImagePointerDown = (e: React.PointerEvent) => {
    if (!canEdit || !referenceSnapshot) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      // Pointer no longer active or already captured — ignore.
    }
    const { x, y } = toPercentFromEvent(e);
    const rect = updateImageRect();
    if (!rect) return;

    for (let i = zones.length - 1; i >= 0; i -= 1) {
      const zone = zones[i];
      const corners: Array<['tl' | 'tr' | 'bl' | 'br', number, number]> = [
        ['tl', zone.x1, zone.y1],
        ['tr', zone.x2, zone.y1],
        ['bl', zone.x1, zone.y2],
        ['br', zone.x2, zone.y2],
      ];
      const hitCorner = corners.find(([, cx, cy]) => Math.abs(cx - x) <= 2.5 && Math.abs(cy - y) <= 2.5);
      if (hitCorner) {
        setSelectedLocalId(zone.localId);
        dragRef.current = { mode: 'resize', index: i, corner: hitCorner[0], startX: x, startY: y, offsetX: 0, offsetY: 0, baseZone: { ...zone } };
        return;
      }
    }

    const hitIndex = zones.findIndex(zone => pointInZone(zone, x, y));
    if (hitIndex >= 0) {
      const zone = zones[hitIndex];
      setSelectedLocalId(zone.localId);
      dragRef.current = {
        mode: 'move',
        index: hitIndex,
        startX: x,
        startY: y,
        offsetX: x - zone.x1,
        offsetY: y - zone.y1,
        baseZone: { ...zone },
      };
      return;
    }

    const draft: { x1: number; y1: number; x2: number; y2: number; final: boolean } = { x1: x, y1: y, x2: x, y2: y, final: false };
    setDraftZone(draft);
    const newKey = computeNextDeskKey();
    const localId = createLocalId();
    dragRef.current = {
      mode: 'draw',
      index: zones.length,
      startX: x,
      startY: y,
      offsetX: 0,
      offsetY: 0,
      baseZone: {
        localId,
        desk_key: newKey,
        label: newKey,
        x1: x,
        y1: y,
        x2: x,
        y2: y,
        active: true,
        sort_order: zones.length,
      },
      draft,
    };
  };

  const handleImagePointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const { x, y } = toPercentFromEvent(e);

    if (drag.mode === 'draw' && drag.draft) {
      drag.draft.x2 = x;
      drag.draft.y2 = y;
      setDraftZone({ ...drag.draft, final: false });
      return;
    }

    if (drag.mode === 'resize') {
      const base = drag.baseZone;
      setZones(prev => prev.map((zone, idx) => {
        if (idx !== drag.index) return zone;
        const next = { ...zone };
        if (drag.corner === 'tl') { next.x1 = clamp(Math.min(x, base.x2 - 1), 0, 100); next.y1 = clamp(Math.min(y, base.y2 - 1), 0, 100); }
        if (drag.corner === 'tr') { next.x2 = clamp(Math.max(x, base.x1 + 1), 0, 100); next.y1 = clamp(Math.min(y, base.y2 - 1), 0, 100); }
        if (drag.corner === 'bl') { next.x1 = clamp(Math.min(x, base.x2 - 1), 0, 100); next.y2 = clamp(Math.max(y, base.y1 + 1), 0, 100); }
        if (drag.corner === 'br') { next.x2 = clamp(Math.max(x, base.x1 + 1), 0, 100); next.y2 = clamp(Math.max(y, base.y1 + 1), 0, 100); }
        return next;
      }));
      return;
    }

    const dx = x - drag.startX;
    const dy = y - drag.startY;
    const base = drag.baseZone;
    setZones(prev => prev.map((zone, idx) => {
      if (idx !== drag.index) return zone;
      const width = base.x2 - base.x1;
      const height = base.y2 - base.y1;
      const x1 = clamp(base.x1 + dx, 0, Math.max(0, 100 - width));
      const y1 = clamp(base.y1 + dy, 0, Math.max(0, 100 - height));
      return {
        ...zone,
        x1,
        y1,
        x2: x1 + width,
        y2: y1 + height,
      };
    }));
  };

  const commitDrag = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    if (drag.mode === 'draw' && drag.draft) {
      const draft = drag.draft;
      const x1 = Math.min(draft.x1, draft.x2);
      const y1 = Math.min(draft.y1, draft.y2);
      const x2 = Math.max(draft.x1, draft.x2);
      const y2 = Math.max(draft.y1, draft.y2);
      setDraftZone(null);
      if (x2 > x1 && y2 > y1) {
        const newZone: EditableDeskZone = {
          localId: drag.baseZone.localId,
          desk_key: drag.baseZone.desk_key,
          label: drag.baseZone.label,
          x1,
          y1,
          x2,
          y2,
          active: true,
          sort_order: zones.length,
        };
        setZones(prev => [...prev, newZone].sort((a, b) => a.sort_order - b.sort_order));
        setSelectedLocalId(newZone.localId);
      }
    }
  };

  const handleImagePointerUp = (e: React.PointerEvent) => {
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // Capture may already have been released — ignore.
    }
    commitDrag();
  };

  const handleImagePointerCancel = () => {
    dragRef.current = null;
    setDraftZone(null);
  };

  const updateZoneField = (localId: string, field: keyof CameraDeskZone, value: string | boolean | number) => {
    setZones(prev => prev.map(z => (z.localId === localId ? { ...z, [field]: value } : z)));
  };

  const deleteZone = (localId: string) => {
    setZones(prev => prev.filter(z => z.localId !== localId).map((z, idx) => ({ ...z, sort_order: idx })));
    if (selectedLocalId === localId) setSelectedLocalId(null);
  };

  const validateZones = (): string | null => {
    const keys = new Set<string>();
    for (const zone of zones) {
      const deskKey = zone.desk_key.trim();
      if (!deskKey || !DESK_KEY_PATTERN.test(deskKey)) {
        return t('validateDeskKey');
      }
      if (keys.has(deskKey)) return t('validateUniqueKey', { value: deskKey });
      keys.add(deskKey);
      if (!zone.label || !zone.label.trim()) return t('validateLabel');
      const coords = [zone.x1, zone.y1, zone.x2, zone.y2];
      if (!coords.every(v => Number.isFinite(v) && v >= 0 && v <= 100)) return t('validateCoordinates');
      if (zone.x2 <= zone.x1 || zone.y2 <= zone.y1) return t('validateSize');
    }
    return null;
  };

  const handleSave = async () => {
    if (!currentCompany || selectedCameraId == null) return;
    const validationError = validateZones();
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const nextZones: EditableDeskZone[] = zones.map(z => ({
        ...z,
        ...toApiZone(z),
      }));
      const payload = nextZones.map(toApiZone);
      await cameraDeskZonesAPI.updateZones(currentCompany.company_id, selectedCameraId, {
        zones: payload,
      });
      setSuccess(t('saveSuccess'));
      setZones(nextZones);
      setLoadedZones(nextZones.map(z => ({ ...z })));
    } catch (err) {
      console.error('Error saving desk zones:', err);
      setError(t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const restoreZones = () => {
    const restored = loadedZones.map(z => ({ ...z }));
    setZones(restored);
    setSelectedLocalId(prev => (prev && restored.some(z => z.localId === prev) ? prev : restored[0]?.localId ?? null));
    setDraftZone(null);
    setError(null);
  };

  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(zones.map(toApiZone)) !== JSON.stringify(loadedZones.map(toApiZone)),
    [zones, loadedZones]
  );

  if (!canViewSnapshots) {
    return (
      <Layout title={t('title')}>
        <Container className="an-enterprise-page an-page-shell an-data-page visioniq-page">
          <Alert variant="danger">{t('accessDenied')}</Alert>
        </Container>
      </Layout>
    );
  }

  const selectedCamera = cameras.find(c => c.id === selectedCameraId);

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-page-shell an-data-page visioniq-page desk-zones-page">
        <div className="page-header">
          <h2 className="fw-bold mb-0 an-page-title">{t('title')}</h2>
          {selectedCameraId != null && (
            <div className="d-flex align-items-center gap-2">
              {hasUnsavedChanges && (
                <Button variant="outline-secondary" onClick={restoreZones} disabled={saving}>
                  {t('discardChanges')}
                </Button>
              )}
              {canEdit && (
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                  {saving ? <Spinner animation="border" size="sm" /> : t('saveZones')}
                </Button>
              )}
            </div>
          )}
        </div>

        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

        <Card className="mb-4 border-0 shadow-sm an-filter-bar">
          <Card.Body>
            <Row className="align-items-end g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-bold">{tc('labels.camera')}</Form.Label>
                  <Form.Select
                    value={selectedCameraId ?? ''}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      setSelectedCameraId(Number.isNaN(val) ? null : val);
                    }}
                  >
                    <option value="">{tc('labels.selectCamera')}</option>
                    {cameras.map(cam => (
                      <option key={cam.id} value={cam.id}>{cam.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={8}>
                <div className="small text-muted an-desk-zones-hint">
                  <i className="bi bi-question-circle me-1"></i>{t('zonesHint')}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
        ) : selectedCameraId == null ? (
          <Card className="text-center p-5 border-0 shadow-sm">
            <Card.Body>
              <div className="mb-3"><i className="bi bi-camera text-muted" style={{ fontSize: '3rem' }}></i></div>
              <h4>{t('noCameras')}</h4>
              <p className="text-muted">{t('noCamerasHint')}</p>
            </Card.Body>
          </Card>
        ) : referenceSnapshot ? (
          <Row className="g-4">
            <Col lg={7} xl={8}>
              <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white py-3 border-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h5 className="mb-0 fw-bold">
                    <span className="me-1"><i className="bi bi-image"></i></span>
                    {selectedCamera?.name || ''}
                  </h5>
                  <div className="small text-muted">
                    {t('referenceSnapshot')}: {formatDateTimeInCompanyTimezone(referenceSnapshot.captured_at, currentCompany?.timezone || 'UTC')}
                  </div>
                </Card.Header>
                <Card.Body
                  ref={editorSurfaceRef}
                  className="p-0 position-relative an-zone-editor-surface"
                  onPointerDown={handleImagePointerDown}
                  onPointerMove={handleImagePointerMove}
                  onPointerUp={handleImagePointerUp}
                  onPointerCancel={handleImagePointerCancel}
                >
                  <img
                    src={referenceSnapshot.image_url}
                    alt={selectedCamera?.name || ''}
                    className="w-100 d-block"
                    style={{ display: 'block', touchAction: 'none' }}
                    onLoad={() => updateImageRect()}
                  />
                  {zones.map((zone) => (
                    <div
                      key={zone.localId}
                      className={`an-zone-box ${selectedLocalId === zone.localId ? 'an-zone-box-selected' : ''}`}
                      style={{
                        position: 'absolute',
                        top: `${zone.y1}%`,
                        left: `${zone.x1}%`,
                        width: `${zone.x2 - zone.x1}%`,
                        height: `${zone.y2 - zone.y1}%`,
                        pointerEvents: 'none',
                      }}
                    >
                      <div className="an-zone-box-label">{zone.label || zone.desk_key}</div>
                      {selectedLocalId === zone.localId && (
                        <>
                          <div className="an-zone-handle an-zone-handle-tl" />
                          <div className="an-zone-handle an-zone-handle-tr" />
                          <div className="an-zone-handle an-zone-handle-bl" />
                          <div className="an-zone-handle an-zone-handle-br" />
                        </>
                      )}
                    </div>
                  ))}
                  {draftZone && !draftZone.final && (
                    <div
                      className="an-zone-box an-zone-box-draft"
                      style={{
                        position: 'absolute',
                        top: `${Math.min(draftZone.y1, draftZone.y2)}%`,
                        left: `${Math.min(draftZone.x1, draftZone.x2)}%`,
                        width: `${Math.abs(draftZone.x2 - draftZone.x1)}%`,
                        height: `${Math.abs(draftZone.y2 - draftZone.y1)}%`,
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                </Card.Body>
                {!canEdit && (
                  <Card.Footer className="small text-muted bg-white border-0">
                    {t('readOnlyHint')}
                  </Card.Footer>
                )}
              </Card>
            </Col>

            <Col lg={5} xl={4}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white py-3 border-0">
                  <h5 className="mb-0 fw-bold">{t('zonesList')} <Badge bg="secondary">{zones.length}</Badge></h5>
                </Card.Header>
                <Card.Body className="p-0">
                  {zones.length === 0 ? (
                    <div className="text-center py-5 px-3 text-muted small">
                      <i className="bi bi-bounding-box mb-2 d-block" style={{ fontSize: '2rem' }}></i>
                      {t('noZonesYet')}
                    </div>
                  ) : (
                    <div className="table-responsive an-panel an-data-table desk-zones-table">
                      <Table hover className="mb-0 align-middle">
                        <thead className="bg-light">
                          <tr>
                            <th style={{ width: '120px' }}>{t('deskKey')}</th>
                            <th>{t('label')}</th>
                            <th style={{ width: '80px' }} className="text-center">{t('sortOrder')}</th>
                            <th style={{ width: '60px' }} className="text-center">{tc('labels.active')}</th>
                            <th style={{ width: '110px' }} className="text-end">{t('actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {zones.map(zone => (
                            <tr
                              key={zone.localId}
                              className={selectedLocalId === zone.localId ? 'table-active' : ''}
                              onClick={() => setSelectedLocalId(zone.localId)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td>
                                <Form.Control
                                  size="sm"
                                  type="text"
                                  value={zone.desk_key}
                                  disabled={!canEdit}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => updateZoneField(zone.localId, 'desk_key', e.target.value)}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  type="text"
                                  value={zone.label}
                                  disabled={!canEdit}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => updateZoneField(zone.localId, 'label', e.target.value)}
                                />
                              </td>
                              <td className="text-center">
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  min={0}
                                  value={zone.sort_order}
                                  disabled={!canEdit}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => updateZoneField(zone.localId, 'sort_order', Number(e.target.value) || 0)}
                                />
                              </td>
                              <td className="text-center">
                                <Form.Check
                                  type="switch"
                                  checked={zone.active}
                                  disabled={!canEdit}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => updateZoneField(zone.localId, 'active', e.target.checked)}
                                />
                              </td>
                              <td className="text-end">
                                {canEdit && (
                                  <Button
                                    variant="link"
                                    className="text-danger p-1 text-decoration-none"
                                    onClick={(e) => { e.stopPropagation(); deleteZone(zone.localId); }}
                                  >
                                    <i className="bi bi-trash"></i>
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
                <Card.Footer className="bg-white border-0 small text-muted">
                  {t('sortOrderHint')}
                </Card.Footer>
              </Card>
            </Col>
          </Row>
        ) : (
          <Card className="text-center p-5 border-0 shadow-sm">
            <Card.Body>
              <div className="mb-4"><i className="bi bi-camera text-muted" style={{ fontSize: '3rem' }}></i></div>
              <h4>{t('noSnapshotTitle')}</h4>
              <p className="text-muted">{t('noSnapshotHint')}</p>
              <Button variant="primary" href={`/company/${currentCompany?.company_id}/settings/snapshot-settings`}>
                {tc('nav.snapshotSettings')}
              </Button>
            </Card.Body>
          </Card>
        )}
      </Container>
    </Layout>
  );
};

export default DeskZonesPage;